import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { UserCircleIcon, BuildingOfficeIcon, ShieldCheckIcon, KeyIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, multiFactor } from 'firebase/auth';
import MFASetup from '../auth/MFASetup';

interface UserProfile {
  displayName: string;
  email: string;
  photoURL?: string;
  phone?: string;
}

interface Organization {
  name: string;
  taxNumber: string;
  registrationNumber: string;
  isActive: boolean;
}

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({
    displayName: user?.displayName || '',
    email: user?.email || '',
    photoURL: user?.photoURL,
    phone: ''
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [hasMFA, setHasMFA] = useState(false);
  const [isLoadingMFA, setIsLoadingMFA] = useState(true);
  const [showDisableMFAModal, setShowDisableMFAModal] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfile(prev => ({
            ...prev,
            phone: userData.phone || ''
          }));

          // Szervezet adatainak betöltése
          if (userData.organizationId) {
            const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
            if (orgDoc.exists()) {
              const orgData = orgDoc.data();
              setOrganization({
                name: orgData.name,
                taxNumber: orgData.taxNumber,
                registrationNumber: orgData.registrationNumber,
                isActive: orgData.isActive
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [user?.id]);

  const checkMFAStatus = async () => {
    if (!user) {
      setIsLoadingMFA(false);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const enrolledFactors = multiFactor(currentUser).enrolledFactors;
        setHasMFA(enrolledFactors.length > 0);
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setIsLoadingMFA(false);
    }
  };

  useEffect(() => {
    checkMFAStatus();
  }, [user]);

  useEffect(() => {
    if (showMFASetup) {
      checkMFAStatus();
    }
  }, [showMFASetup]);

  const handleProfileSave = async () => {
    if (!user?.id) return;

    try {
      await updateDoc(doc(db, 'users', user.id), {
        phone: profile.phone
      });

      toast.success(t('common.messages.profile.updateSuccess'));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('common.messages.profile.updateError'));
    }
  };

  const handlePasswordChange = async () => {
    if (!user?.email) return;

    if (passwordData.new !== passwordData.confirm) {
      toast.error('Az új jelszavak nem egyeznek!');
      return;
    }

    if (passwordData.new.length < 6) {
      toast.error('Az új jelszó legalább 6 karakter hosszú legyen!');
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      // Újra-hitelesítés
      const credential = EmailAuthProvider.credential(user.email, passwordData.current);
      await reauthenticateWithCredential(currentUser, credential);

      // Jelszó frissítése
      await updatePassword(currentUser, passwordData.new);

      toast.success('Jelszó sikeresen megváltoztatva!');
      setShowPasswordChange(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Hibás jelenlegi jelszó!');
      } else {
        toast.error('Hiba a jelszó módosítása során: ' + error.message);
      }
    }
  };

  const confirmDisableMFA = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const enrolledFactors = multiFactor(currentUser).enrolledFactors;
      if (enrolledFactors.length > 0) {
        await multiFactor(currentUser).unenroll(enrolledFactors[0]);
        setHasMFA(false);
        setShowDisableMFAModal(false);
        toast.success('Kétfaktoros hitelesítés letiltva!');
      }
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      toast.error('Hiba az MFA letiltása során: ' + error.message);
    }
  };

  return (
    <div className="space-y-8 pb-20 sm:pb-6">
      <h1 className="text-2xl font-semibold text-gray-800">{t('common.profile.title')}</h1>

      {/* Profil adatok szekció */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {profile.photoURL ? (
              <img
                src={profile.photoURL}
                alt={profile.displayName}
                className="h-10 w-10 rounded-full"
              />
            ) : (
              <UserCircleIcon className="h-10 w-10 text-blue-500" />
            )}
            <h2 className="text-xl font-medium text-gray-800">{t('common.profile.personalInfo')}</h2>
          </div>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {isEditing ? t('common.actions.cancel') : t('common.actions.edit')}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.profile.fullName')}
            </label>
            <input
              type="text"
              value={profile.displayName}
              disabled={true}
              className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.profile.email')}
            </label>
            <input
              type="email"
              value={profile.email}
              disabled={true}
              className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.profile.phone')}
            </label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              disabled={!isEditing}
              placeholder="+36201234567"
              className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>
        </div>

        {isEditing && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleProfileSave}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {t('common.actions.save')}
            </button>
          </div>
        )}
      </div>

      {/* Szervezet adatok szekció */}
      {organization && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <BuildingOfficeIcon className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-medium text-gray-800">{t('common.profile.organization')}</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.profile.organizationName')}
              </label>
              <input
                type="text"
                value={organization.name}
                disabled={true}
                className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.profile.taxNumber')}
              </label>
              <input
                type="text"
                value={organization.taxNumber}
                disabled={true}
                className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.profile.registrationNumber')}
              </label>
              <input
                type="text"
                value={organization.registrationNumber}
                disabled={true}
                className="w-full bg-gray-50 border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.profile.organizationStatus')}
              </label>
              <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                organization.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {organization.isActive ? t('status.active') : t('status.inactive')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Biztonság szekció */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <ShieldCheckIcon className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-medium text-gray-800">Biztonság</h2>
        </div>

        <div className="space-y-6">
          {/* Jelszó módosítás */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <KeyIcon className="h-5 w-5 text-gray-500" />
                <h3 className="text-lg font-medium text-gray-800">Jelszó módosítása</h3>
              </div>
              <button
                onClick={() => setShowPasswordChange(!showPasswordChange)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {showPasswordChange ? 'Mégse' : 'Jelszó módosítása'}
              </button>
            </div>

            {showPasswordChange && (
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jelenlegi jelszó
                  </label>
                  <input
                    type="password"
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    className="w-full bg-white border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Új jelszó
                  </label>
                  <input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    className="w-full bg-white border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Új jelszó megerősítése
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    className="w-full bg-white border border-gray-300 text-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handlePasswordChange}
                    disabled={!passwordData.current || !passwordData.new || !passwordData.confirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Jelszó mentése
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Kétfaktoros hitelesítés */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <LockClosedIcon className="h-5 w-5 text-gray-500" />
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Kétfaktoros hitelesítés (2FA)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Extra védelem a fiókodnak Google Authenticator alkalmazással
                  </p>
                </div>
              </div>
              {!isLoadingMFA && (
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    hasMFA ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {hasMFA ? '✓ Bekapcsolva' : 'Kikapcsolva'}
                  </span>
                  {hasMFA ? (
                    <button
                      onClick={() => setShowDisableMFAModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Letiltás
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowMFASetup(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Bekapcsolás
                    </button>
                  )}
                </div>
              )}
            </div>

            {hasMFA && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>✓ Védve vagy!</strong> A fiókod védve van kétfaktoros hitelesítéssel. Bejelentkezéskor meg kell adnod a 6 számjegyű kódot az Authenticator alkalmazásodból.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MFA Setup Modal */}
      {showMFASetup && (
        <MFASetup
          onComplete={() => {
            setShowMFASetup(false);
            checkMFAStatus(); // Frissítse az MFA státuszt
            setHasMFA(true);
            toast.success('Kétfaktoros hitelesítés sikeresen bekapcsolva!');
          }}
          onCancel={() => setShowMFASetup(false)}
        />
      )}

      {/* MFA Disable Confirmation Modal */}
      {showDisableMFAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Kétfaktoros hitelesítés letiltása
              </h2>
              <p className="text-sm text-gray-600">
                Biztosan letiltod a kétfaktoros hitelesítést?
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Figyelem!</strong> A kétfaktoros hitelesítés letiltásával a fiókod kevésbé lesz biztonságos. Csak jelszóval tudsz majd bejelentkezni.
                </p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>🔒 Fontos!</strong> A következő bejelentkezéskor <strong>kötelező lesz újra bekapcsolnod</strong> a kétfaktoros hitelesítést. Ez egy biztonsági követelmény.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDisableMFAModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Mégse
              </button>
              <button
                onClick={confirmDisableMFA}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Letiltás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};